import { isAndroid, isIOS } from "../backend/utils/detect-device";

/**
 * The device a link was shared from, carried in the `s=` query parameter next to the sharer's `r=`. The codes
 * are the ones X puts on a shared tweet's address (s=46 iPhone, s=20 browser, s=19 Android), so one reading of
 * the number works for both.
 */
export const SHARER_DEVICE_QUERY_PARAMETER = "s";
// Remembers the device of whoever referred this visitor, beside `ref_uid`, until the visitor signs up.
export const SHARER_DEVICE_LOCAL_STORAGE_KEY = "ref_sharer_device";

export enum SharerDevice {
  IPhone = "iphone",
  Browser = "browser",
  Android = "android",
}

const SHARER_DEVICE_CODES: Record<SharerDevice, string> = {
  [SharerDevice.IPhone]: "46",
  [SharerDevice.Browser]: "20",
  [SharerDevice.Android]: "19",
};

/** Any other value (a stray `s=` from some other site, a made-up number) is not a device. */
export function sharerDeviceFromCode(code: string | null | undefined): SharerDevice | null {
  const matchingDevice = Object.values(SharerDevice).find(sharerDevice => SHARER_DEVICE_CODES[sharerDevice] === code);
  return matchingDevice ?? null;
}

/** An iPad counts as an iPhone, like the Jungle iOS app that runs on both. */
export function getCurrentSharerDevice(): SharerDevice {
  if (isIOS()) return SharerDevice.IPhone;
  if (isAndroid()) return SharerDevice.Android;
  return SharerDevice.Browser;
}

export function getCurrentSharerDeviceCode(): string {
  return SHARER_DEVICE_CODES[getCurrentSharerDevice()];
}

export function setSharerDeviceParameter(searchParams: URLSearchParams): void {
  searchParams.set(SHARER_DEVICE_QUERY_PARAMETER, getCurrentSharerDeviceCode());
}

/** For links written as template strings: `${link}?r=${code}&${sharerDeviceQueryPair()}`. */
export function sharerDeviceQueryPair(): string {
  return `${SHARER_DEVICE_QUERY_PARAMETER}=${getCurrentSharerDeviceCode()}`;
}

/** The sharer's device stored on this device when the visitor arrived through a referral link, if any. */
export function getStoredSharerDevice(): SharerDevice | null {
  try {
    const storedCode = localStorage.getItem(SHARER_DEVICE_LOCAL_STORAGE_KEY);
    return sharerDeviceFromCode(storedCode);
  } catch {
    return null;
  }
}

/** The `?r=…&s=…` an in-app address change keeps from the page's current query (nothing when there is no `r`). */
export function referralQueryFromSearchParams(searchParams: URLSearchParams): string {
  const referralCode = searchParams.get("r");
  if (!referralCode) return "";
  const sharerDeviceCode = searchParams.get(SHARER_DEVICE_QUERY_PARAMETER);
  const carriedParameters = new URLSearchParams({ r: referralCode });
  if (sharerDeviceFromCode(sharerDeviceCode)) carriedParameters.set(SHARER_DEVICE_QUERY_PARAMETER, sharerDeviceCode as string);
  return `?${carriedParameters.toString()}`;
}
